import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import DashboardLayout from "../components/Layout/DashboardLayout";
import { CardGridSkeleton, Skeleton } from "../components/ui/Skeleton";
import { clientsApi } from "../api/clientsApi";
import {
  reportsApi,
  ReportsDashboardAnalytics,
  ClientOutcomeRow,
} from "../api/reportsApi";
import { AiPerformance, Client, RevenueReport, ScoreHistoryPoint } from "../types";

const RANGE_OPTIONS = [
  { label: "Last 30 days", value: 30 },
  { label: "Last 90 days", value: 90 },
  { label: "Last 6 months", value: 180 },
  { label: "Last year", value: 365 },
] as const;

const STATUS_CHART_COLORS = [
  "#22c55e",
  "#3b82f6",
  "#f59e0b",
  "#a855f7",
  "#ec4899",
  "#14b8a6",
  "#f97316",
  "#64748b",
];

function averageScoreByPeriod(points: ScoreHistoryPoint[]): { label: string; value: number }[] {
  const map = new Map<string, { sum: number; n: number }>();
  for (const p of points) {
    const key = p.period.length >= 7 ? p.period.slice(0, 7) : p.period;
    const e = map.get(key) ?? { sum: 0, n: 0 };
    e.sum += p.score;
    e.n += 1;
    map.set(key, e);
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([label, { sum, n }]) => ({ label, value: Math.round((sum / n) * 10) / 10 }));
}

function escapeCsvCell(v: string | number): string {
  const s = String(v);
  if (/[",\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

const ReportsPage: React.FC = () => {
  const [rangeDays, setRangeDays] = useState<number>(90);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [analytics, setAnalytics] = useState<ReportsDashboardAnalytics | null>(null);
  const [revenue, setRevenue] = useState<RevenueReport | null>(null);
  const [aiPerf, setAiPerf] = useState<AiPerformance | null>(null);
  const [clientOutcomes, setClientOutcomes] = useState<ClientOutcomeRow[]>([]);

  const [clients, setClients] = useState<Client[]>([]);
  const [clientId, setClientId] = useState("");
  const [scoreSeries, setScoreSeries] = useState<{ label: string; value: number }[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [dash, rev, ai, outcomes, cls] = await Promise.all([
        reportsApi.getReportsDashboard(rangeDays),
        reportsApi.getRevenue(rangeDays),
        reportsApi.getAiPerformance(rangeDays),
        reportsApi.getReportsClients(),
        clientsApi.getAll(),
      ]);
      setAnalytics(dash);
      setRevenue(rev);
      setAiPerf(ai);
      setClientOutcomes(outcomes);
      setClients(cls);
      setClientId((prev) => {
        if (prev && cls.some((c) => c.id === prev)) {
          return prev;
        }
        return cls[0]?.id ?? "";
      });
    } catch {
      setError("Failed to load reports. Check the API and try again.");
    } finally {
      setLoading(false);
    }
  }, [rangeDays]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!clientId) {
      setScoreSeries([]);
      return;
    }
    const run = async () => {
      try {
        const pts = await reportsApi.getClientScoreHistory(clientId, rangeDays);
        setScoreSeries(averageScoreByPeriod(pts));
      } catch {
        setScoreSeries([]);
      }
    };
    void run();
  }, [clientId, rangeDays]);

  const statusPieData = useMemo(() => {
    if (!analytics?.statusBreakdown?.length) {
      return [];
    }
    return analytics.statusBreakdown
      .map((s) => ({ name: s.status, value: s.count }))
      .filter((d) => d.value > 0);
  }, [analytics]);

  const bureauChartData = useMemo(() => {
    if (!analytics?.bureauStats?.length) {
      return [];
    }
    return analytics.bureauStats.map((b) => ({
      name: b.bureau,
      total: b.total,
      resolved: b.resolved,
      inPipeline: b.inDispute,
    }));
  }, [analytics]);

  const monthlyChartData = useMemo(() => {
    if (!analytics?.monthlyActivity?.length) {
      return [];
    }
    return analytics.monthlyActivity.map((m) => ({
      month: m.month,
      lettersSent: m.lettersSent,
      newClients: m.newClients,
      itemsResolved: m.itemsResolved,
    }));
  }, [analytics]);

  const exportCsv = () => {
    if (!analytics || !aiPerf || !revenue) {
      return;
    }
    const lines: string[][] = [
      ["IntelliWarrior Reports Export"],
      ["Score trend range days", String(rangeDays)],
      [],
      ["Analytics (live tenant data)"],
      ["Total clients", String(analytics.totalClients)],
      ["Active disputes (open pipeline)", String(analytics.activeDisputes)],
      ["Awaiting response", String(analytics.awaitingResponse)],
      ["Resolved disputes", String(analytics.resolvedDisputes)],
      ["Total dispute items", String(analytics.totalDisputeItems)],
      ["Resolution rate %", String(analytics.resolutionRate)],
      ["Average score change (range)", String(analytics.averageScoreChange)],
      ["Estimated MRR", String(analytics.estimatedMonthlyRevenue)],
      ["Revenue per client", String(analytics.revenuePerClient)],
      [],
      ["Status breakdown"],
      ...analytics.statusBreakdown.map((s) => [s.status, String(s.count)]),
      [],
      ["Bureau stats"],
      ...analytics.bureauStats.map((b) => [b.bureau, "total", String(b.total), "resolved", String(b.resolved)]),
      [],
      ["Monthly activity"],
      ["Month", "Letters", "New clients", "Resolved"],
      ...analytics.monthlyActivity.map((m) => [
        m.month,
        String(m.lettersSent),
        String(m.newClients),
        String(m.itemsResolved),
      ]),
      [],
      ["AI performance"],
      ["Analyses run", String(aiPerf.analysesRun)],
      ["Avg priority (1-4)", String(aiPerf.avgPriorityLevel)],
      ...aiPerf.topDisputeTypes.map((t) => [`Top type: ${t.name}`, String(t.count)]),
      [],
      ["Revenue"],
      ["MRR", String(revenue.mrr)],
      ["New subscribers (period)", String(revenue.newSubscribers)],
      ["Churned (period)", String(revenue.churned)],
      ["Est. LTV", String(revenue.estimatedLifetimeValue)],
    ];

    lines.push([], ["Avg score trend (tenant)"], ["Month", "Avg score"]);
    analytics.averageScoreTrend.forEach((t) => lines.push([t.label, String(t.value)]));

    const csv = lines.map((row) => row.map((c) => escapeCsvCell(c)).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `intelliwarrior-reports-${rangeDays}d.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const cardClass = "rounded-xl border border-slate-800 bg-slate-900/90 p-4 shadow-lg shadow-black/20";
  const selectClass =
    "rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none ring-blue-500 focus:ring-2";

  if (loading && !analytics) {
    return (
      <DashboardLayout>
        <div className="space-y-6" aria-busy="true" aria-label="Loading reports">
          <Skeleton className="h-10 w-64" />
          <CardGridSkeleton cards={6} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-white">Reports</h1>
            <p className="text-sm text-slate-400">
              Live tenant metrics from the database — disputes, scores, activity, and revenue estimates.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-xs text-slate-500">
              Score trend range
              <select
                className={`${selectClass} ml-2`}
                value={rangeDays}
                onChange={(e) => setRangeDays(Number(e.target.value))}
              >
                {RANGE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={exportCsv}
              disabled={!analytics}
              className="rounded-lg border border-blue-500/50 bg-blue-950/40 px-4 py-2 text-sm font-medium text-blue-200 hover:bg-blue-900/40 disabled:opacity-40"
            >
              Export CSV
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-950/30 px-4 py-3 text-sm text-amber-100">{error}</div>
        )}

        {analytics && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {[
              ["Total clients", analytics.totalClients],
              ["Active disputes", analytics.activeDisputes],
              ["Resolved", analytics.resolvedDisputes],
              ["Resolution rate", `${analytics.resolutionRate}%`],
              ["Avg score change", analytics.averageScoreChange],
              ["Est. MRR", `$${Number(analytics.estimatedMonthlyRevenue).toLocaleString()}`],
            ].map(([label, val]) => (
              <div key={String(label)} className={cardClass}>
                <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
                <p className="mt-1 text-xl font-semibold text-white">{val}</p>
                {label === "Est. MRR" && (
                  <p className="mt-1 text-[10px] text-slate-500">
                    ~${Number(analytics.revenuePerClient).toFixed(2)} / client · modeled (see API config)
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {analytics && (
          <p className="text-xs text-slate-500">
            Awaiting bureau response: <span className="text-slate-300">{analytics.awaitingResponse}</span> · Total dispute
            items: <span className="text-slate-300">{analytics.totalDisputeItems}</span>
          </p>
        )}

        <div className="grid gap-6 xl:grid-cols-2">
          <div className={cardClass}>
            <h2 className="mb-1 text-sm font-semibold text-sky-300">Monthly activity (last 6 months, UTC)</h2>
            <p className="mb-3 text-xs text-slate-500">Letters sent, new clients, items resolved — sparse data is expected on small tenants.</p>
            <div className="h-72 w-full min-w-0">
              {monthlyChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthlyChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="month" tick={{ fill: "#94a3b8", fontSize: 10 }} />
                    <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ backgroundColor: "#0f172a", border: "1px solid #334155", borderRadius: "8px" }}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="lettersSent" stroke="#60a5fa" strokeWidth={2} name="Letters sent" dot />
                    <Line type="monotone" dataKey="newClients" stroke="#34d399" strokeWidth={2} name="New clients" dot />
                    <Line type="monotone" dataKey="itemsResolved" stroke="#f472b6" strokeWidth={2} name="Resolved" dot />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="py-20 text-center text-sm text-slate-500">No monthly activity yet.</p>
              )}
            </div>
          </div>

          <div className={cardClass}>
            <h2 className="mb-3 text-sm font-semibold text-violet-300">Disputes by status</h2>
            <div className="flex h-80 flex-col items-center justify-center gap-4 lg:flex-row">
              <div className="h-64 w-full min-w-0 lg:w-1/2">
                {statusPieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusPieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={88}
                        label={({ name, percent }) => `${name}: ${Math.round((percent ?? 0) * 100)}%`}
                      >
                        {statusPieData.map((_, i) => (
                          <Cell key={String(i)} fill={STATUS_CHART_COLORS[i % STATUS_CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ backgroundColor: "#0f172a", border: "1px solid #334155", borderRadius: "8px" }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="py-16 text-center text-sm text-slate-500">No dispute items yet.</p>
                )}
              </div>
              <div className="w-full lg:w-1/2">
                {statusPieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                      data={statusPieData}
                      layout="vertical"
                      margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                      <XAxis type="number" tick={{ fill: "#94a3b8", fontSize: 11 }} allowDecimals={false} />
                      <YAxis type="category" dataKey="name" width={100} tick={{ fill: "#94a3b8", fontSize: 9 }} />
                      <Tooltip
                        contentStyle={{ backgroundColor: "#0f172a", border: "1px solid #334155", borderRadius: "8px" }}
                      />
                      <Bar dataKey="value" name="Count" radius={[0, 6, 6, 0]}>
                        {statusPieData.map((_, i) => (
                          <Cell key={String(i)} fill={STATUS_CHART_COLORS[i % STATUS_CHART_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className={cardClass}>
          <h2 className="mb-1 text-sm font-semibold text-amber-300">Bureau exposure</h2>
          <p className="mb-3 text-xs text-slate-500">Items disputed with each bureau (from dispute flags).</p>
          <div className="h-72 w-full min-w-0">
            {bureauChartData.length > 0 && bureauChartData.some((b) => b.total > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bureauChartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#0f172a", border: "1px solid #334155", borderRadius: "8px" }}
                  />
                  <Legend />
                  <Bar dataKey="total" fill="#475569" name="Total" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="resolved" fill="#22c55e" name="Resolved" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="inPipeline" fill="#f59e0b" name="In pipeline" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="py-20 text-center text-sm text-slate-500">No bureau-flagged disputes yet.</p>
            )}
          </div>
        </div>

        <div className="grid gap-6 xl:grid-cols-2">
          <div className={cardClass}>
            <h2 className="mb-1 text-sm font-semibold text-blue-300">Revenue — MRR trend</h2>
            <p className="mb-3 text-xs text-slate-500">Modeled per active client (see API config Reports:MrrPerActiveClient).</p>
            <div className="h-72 w-full min-w-0">
              {revenue && revenue.mrrTrend.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={revenue.mrrTrend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="mrrGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                    <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                    <Tooltip
                      contentStyle={{ backgroundColor: "#0f172a", border: "1px solid #334155", borderRadius: "8px" }}
                      labelStyle={{ color: "#e2e8f0" }}
                    />
                    <Area type="monotone" dataKey="value" stroke="#60a5fa" fill="url(#mrrGrad)" name="MRR" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <p className="py-20 text-center text-sm text-slate-500">No revenue data for this range.</p>
              )}
            </div>
          </div>

          <div className={cardClass}>
            <h2 className="mb-1 text-sm font-semibold text-emerald-300">Subscriber growth</h2>
            <p className="mb-3 text-xs text-slate-500">Cumulative clients created through each month.</p>
            <div className="h-72 w-full min-w-0">
              {revenue && revenue.subscriberGrowth.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={revenue.subscriberGrowth} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                    <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ backgroundColor: "#0f172a", border: "1px solid #334155", borderRadius: "8px" }}
                    />
                    <Line type="monotone" dataKey="value" stroke="#34d399" strokeWidth={2} dot={{ fill: "#34d399" }} name="Clients" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="py-20 text-center text-sm text-slate-500">No subscriber history.</p>
              )}
            </div>
          </div>
        </div>

        <div className={cardClass}>
          <h2 className="mb-1 text-sm font-semibold text-sky-300">Tenant average score trend</h2>
          <p className="mb-3 text-xs text-slate-500">From ScoreHistory (same range as score change above).</p>
          <div className="h-72 w-full min-w-0">
            {analytics && analytics.averageScoreTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={analytics.averageScoreTrend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                  <YAxis domain={["auto", "auto"]} tick={{ fill: "#94a3b8", fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#0f172a", border: "1px solid #334155", borderRadius: "8px" }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="value" stroke="#38bdf8" strokeWidth={2} name="Avg score" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="py-20 text-center text-sm text-slate-500">Add ScoreHistory data to see tenant-wide trends.</p>
            )}
          </div>
        </div>

        <div className={cardClass}>
          <h2 className="mb-3 text-sm font-semibold text-lime-300">Client outcomes</h2>
          {clientOutcomes.length === 0 ? (
            <p className="text-sm text-slate-500">No clients in this tenant.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm text-slate-200">
                <thead className="border-b border-slate-800 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="py-2 pr-4">Client</th>
                    <th className="py-2 pr-4">Score Δ</th>
                    <th className="py-2 pr-4">Avg days to resolve</th>
                    <th className="py-2 pr-4">Disputes</th>
                    <th className="py-2 pr-4">Resolved</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {clientOutcomes.map((row) => (
                    <tr key={row.clientId}>
                      <td className="py-2 pr-4">
                        <Link to={`/clients/${row.clientId}`} className="font-medium text-blue-300 hover:text-blue-200">
                          {row.clientName}
                        </Link>
                      </td>
                      <td className="py-2 pr-4 text-slate-300">
                        {row.scoreImprovement != null
                          ? `${row.scoreImprovement > 0 ? "+" : ""}${row.scoreImprovement}`
                          : "—"}
                      </td>
                      <td className="py-2 pr-4 text-slate-300">
                        {row.averageDaysToResolution != null ? row.averageDaysToResolution : "—"}
                      </td>
                      <td className="py-2 pr-4">{row.totalDisputes}</td>
                      <td className="py-2 pr-4">{row.resolvedDisputes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className={cardClass}>
          <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-sm font-semibold text-cyan-300">Client score improvement</h2>
              <p className="text-xs text-slate-500">Average of three bureaus by month for the selected client.</p>
            </div>
            <select className={selectClass} value={clientId} onChange={(e) => setClientId(e.target.value)}>
              {clients.length === 0 ? (
                <option value="">No clients</option>
              ) : (
                clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.firstName} {c.lastName}
                  </option>
                ))
              )}
            </select>
          </div>
          <div className="h-72 w-full min-w-0">
            {scoreSeries.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={scoreSeries} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                  <YAxis domain={[300, 850]} tick={{ fill: "#94a3b8", fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#0f172a", border: "1px solid #334155", borderRadius: "8px" }}
                  />
                  <Line type="monotone" dataKey="value" stroke="#a78bfa" strokeWidth={2} name="Avg score" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="py-20 text-center text-sm text-slate-500">Select a client with score history or analyses.</p>
            )}
          </div>
        </div>

        <div className={cardClass}>
          <h2 className="mb-3 text-sm font-semibold text-amber-300">AI performance</h2>
          {aiPerf && (
            <div className="grid gap-6 lg:grid-cols-3">
              <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-4">
                <p className="text-xs text-slate-500">Analyses run</p>
                <p className="mt-1 text-3xl font-bold text-white">{aiPerf.analysesRun}</p>
              </div>
              <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-4">
                <p className="text-xs text-slate-500">Avg priority level (1–4)</p>
                <p className="mt-1 text-3xl font-bold text-white">{aiPerf.avgPriorityLevel}</p>
              </div>
              <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-4">
                <p className="text-xs text-slate-500">Top dispute types</p>
                <ul className="mt-2 space-y-1 text-sm text-slate-300">
                  {aiPerf.topDisputeTypes.map((t) => (
                    <li key={t.name}>
                      <span className="text-white">{t.name}</span>
                      <span className="text-slate-500"> — {t.count}</span>
                    </li>
                  ))}
                  {aiPerf.topDisputeTypes.length === 0 && <li className="text-slate-500">No data</li>}
                </ul>
              </div>
              <div className="h-64 w-full min-w-0 lg:col-span-3">
                {aiPerf.topDisputeTypes.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={aiPerf.topDisputeTypes.map((t) => ({ name: t.name, count: t.count }))}
                      margin={{ top: 8, right: 8, left: 0, bottom: 40 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} angle={-20} textAnchor="end" height={48} />
                      <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{ backgroundColor: "#0f172a", border: "1px solid #334155", borderRadius: "8px" }}
                      />
                      <Bar dataKey="count" fill="#f59e0b" radius={[6, 6, 0, 0]} name="Disputes" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ReportsPage;
